// Unified Road Health Score Calculation System
// Segment-first approach with consistent enhanced ALR scoring throughout

// Base configuration
const DEFAULT_SMOOTHNESS_WEIGHT = 0.60;
const DEFAULT_ALR_WEIGHT = 0.25;
const DEFAULT_CONSISTENCY_WEIGHT = 0.15;

// Default scoring weights
export const DEFAULT_WEIGHTS = {
  smoothness: 0.60,
  alr: 0.25,
  consistency: 0.15
};

// Health score categories with muted colors
export const HEALTH_CATEGORIES = {
  EXCELLENT: { 
    min: 90, 
    label: 'Excellent', 
    color: '#065f46',
    bgColor: '#ecfdf5'     
  },
  GOOD: { 
    min: 80, 
    label: 'Good', 
    color: '#059669',
    bgColor: '#f0fdf4'     
  },
  FAIR: { 
    min: 70, 
    label: 'Fair', 
    color: '#d97706',
    bgColor: '#fffbeb'     
  },
  POOR: { 
    min: 60, 
    label: 'Poor', 
    color: '#dc2626',
    bgColor: '#fef2f2'     
  },
  VERY_POOR: { 
    min: 0, 
    label: 'Very Poor', 
    color: '#991b1b',
    bgColor: '#fef2f2'     
  }
};

// Build standard thresholds
export const buildThresholds = (iriThreshold) => {
  return {
    EXCELLENT: { maxIRI: iriThreshold * 0.20 },  // ≤ 32
    GOOD: { maxIRI: iriThreshold * 0.40 },       // ≤ 64
    FAIR: { maxIRI: iriThreshold * 0.60 },       // ≤ 96
    POOR: { maxIRI: iriThreshold * 0.80 },       // ≤ 128
    VERY_POOR: { maxIRI: iriThreshold * 0.90 },  // ≤ 144
    FAIL: { maxIRI: iriThreshold }               // ≤ 160
  };
};

// ============================================================================
// CORE SCORING FUNCTIONS
// ============================================================================

// Non-linear smoothness scoring with exponential decay
export const getSmoothnessScoreNonLinear = (iri, thresholds) => {
  iri = Math.max(0, iri);
  
  if (iri <= thresholds.EXCELLENT.maxIRI) return 100;
  if (iri >= thresholds.FAIL.maxIRI) return 0;
  
  if (iri <= thresholds.GOOD.maxIRI) {
    const ratio = (iri - thresholds.EXCELLENT.maxIRI) / (thresholds.GOOD.maxIRI - thresholds.EXCELLENT.maxIRI);
    return 100 - (10 * Math.pow(ratio, 1.5));
  }
  
  if (iri <= thresholds.FAIR.maxIRI) {
    const ratio = (iri - thresholds.GOOD.maxIRI) / (thresholds.FAIR.maxIRI - thresholds.GOOD.maxIRI);
    return 90 - (10 * Math.pow(ratio, 1.2));
  }
  
  if (iri <= thresholds.POOR.maxIRI) {
    const ratio = (iri - thresholds.FAIR.maxIRI) / (thresholds.POOR.maxIRI - thresholds.FAIR.maxIRI);
    return 80 - (10 * Math.pow(ratio, 1.1));
  }
  
  const ratio = (iri - thresholds.POOR.maxIRI) / (thresholds.FAIL.maxIRI - thresholds.POOR.maxIRI);
  return Math.max(0, 70 * Math.pow(1 - ratio, 2));
};

// Legacy linear smoothness scoring
export const getSmoothnessBand = (iri, thresholds) => {
  iri = Math.max(0, iri);
  if (iri <= thresholds.EXCELLENT.maxIRI) return 100;
  if (iri <= thresholds.GOOD.maxIRI) return 90;
  if (iri <= thresholds.FAIR.maxIRI) return 80;
  if (iri <= thresholds.POOR.maxIRI) return 70;
  if (iri <= thresholds.FAIL.maxIRI) {
    const range = thresholds.FAIL.maxIRI - thresholds.POOR.maxIRI;
    const excess = iri - thresholds.POOR.maxIRI;
    return Math.max(0, 70 - (70 * excess / range));
  }
  return 0;
};

// Consistency scoring based on left/right IRI variance
export const getConsistencyScore = (segment) => {
  const leftIRI = segment.iri_left_in_per_mi || 0;
  const rightIRI = segment.iri_right_in_per_mi || 0;
  const avgIRI = (leftIRI + rightIRI) / 2;
  
  if (avgIRI === 0) return 100;
  
  const difference = Math.abs(leftIRI - rightIRI);
  const percentDifference = difference / avgIRI;
  
  if (percentDifference <= 0.05) return 100;
  if (percentDifference <= 0.10) return 90;
  if (percentDifference <= 0.15) return 80;
  if (percentDifference <= 0.25) return 70;
  if (percentDifference <= 0.40) return 60;
  
  return Math.max(40, 60 - (percentDifference - 0.40) * 100);
};

// Confidence scoring based on measurement quality
export const getConfidenceScore = (segment, measurementQuality = {}) => {
  let confidence = 100;
  
  const lengthFt = segment.distance_ft || 0;
  if (lengthFt < 100) confidence -= 15;
  else if (lengthFt < 200) confidence -= 5;
  
  const variability = measurementQuality.variability || 0;
  if (variability > 0.20) confidence -= 20;
  else if (variability > 0.15) confidence -= 10;
  else if (variability > 0.10) confidence -= 5;
  
  const consistencyScore = getConsistencyScore(segment);
  if (consistencyScore < 70) confidence -= 10;
  
  return Math.max(60, confidence);
};

// ============================================================================
// ENHANCED ALR SCORING SYSTEM
// ============================================================================

// Industry-aligned ALR scoring based on grinding ratio
export const getALRScoreFromGrindRatio = (grindRatio) => {
  if (grindRatio <= 0.005) return 100;   // ≤ 0.5%
  if (grindRatio <= 0.02) return 90;     // 0.6% - 2%
  if (grindRatio <= 0.05) return 75;     // 2.1% - 5%
  if (grindRatio <= 0.08) return 60;     // 5.1% - 8%
  if (grindRatio <= 0.12) return 40;     // 8.1% - 12%
  return 20;                             // > 12%
};

// Calculate severity score based on ALR magnitudes
export const calculateSeverityScore = (alrEvents, iriThreshold) => {
  if (!alrEvents || alrEvents.length === 0) return 100;
  
  const avgSeverity = alrEvents.reduce((sum, e) => sum + (e.magnitude_in || 0), 0) / alrEvents.length;
  const maxSeverity = Math.max(...alrEvents.map(e => e.magnitude_in || 0));
  
  const avgSeverityRatio = avgSeverity / iriThreshold;
  const maxSeverityRatio = maxSeverity / iriThreshold;
  
  const combinedRatio = (avgSeverityRatio * 0.7) + (maxSeverityRatio * 0.3);
  
  if (combinedRatio <= 1.25) return 100;
  if (combinedRatio <= 1.5) return 80;
  if (combinedRatio <= 2.0) return 60;
  if (combinedRatio <= 2.5) return 40;
  return 20;
};

// Calculate density score based on ALRs per mile
export const calculateDensityScore = (alrsPerMile) => {
  if (alrsPerMile <= 2) return 100;
  if (alrsPerMile <= 5) return 90;
  if (alrsPerMile <= 10) return 75;
  if (alrsPerMile <= 15) return 60;
  if (alrsPerMile <= 25) return 40;
  return 20;
};

// Calculate criticality score based on worst individual ALR
export const calculateCriticalityScore = (maxSeverity, iriThreshold) => {
  if (maxSeverity <= iriThreshold * 1.5) return 100;
  if (maxSeverity <= iriThreshold * 2.0) return 80;
  if (maxSeverity <= iriThreshold * 2.5) return 60;
  if (maxSeverity <= iriThreshold * 3.0) return 40;
  return 20;
};

// ============================================================================
// SEGMENT-SPECIFIC ALR ANALYSIS
// ============================================================================

// Check if an event overlaps with a segment
export const eventOverlapsSegment = (event, segment) => {
  const eventStart = event.from_station_ft || 0;
  const eventEnd = event.to_station_ft || eventStart;
  const segmentStart = segment.from_station_ft || 0;
  const segmentEnd = segment.to_station_ft || (segmentStart + (segment.distance_ft || 0));
  
  return !(eventEnd < segmentStart || eventStart > segmentEnd);
};

// Calculate overlapping length between event and segment
export const calculateOverlapLength = (event, segment) => {
  const eventStart = event.from_station_ft || 0;
  const eventEnd = event.to_station_ft || eventStart;
  const segmentStart = segment.from_station_ft || 0;
  const segmentEnd = segment.to_station_ft || (segmentStart + (segment.distance_ft || 0));
  
  if (!eventOverlapsSegment(event, segment)) return 0;
  
  const overlapStart = Math.max(eventStart, segmentStart);
  const overlapEnd = Math.min(eventEnd, segmentEnd);
  
  return Math.max(0, overlapEnd - overlapStart);
};

// UNIFIED: Calculate comprehensive segment ALR data with enhanced scoring
export const calculateSegmentALRData = (segment, allEvents, iriThreshold = 160) => {
  // Filter ALR events above threshold
  const alrEvents = allEvents.filter(ev =>
    ev.event_type === 'LR' && (ev.magnitude_in || 0) > iriThreshold
  );
  
  // Find ALR events that overlap with this segment
  const overlappingALRs = alrEvents.filter(event => 
    eventOverlapsSegment(event, segment)
  );
  
  // Calculate total grinding length within this segment
  const grindingRequiredFt = overlappingALRs.reduce((sum, event) => {
    return sum + calculateOverlapLength(event, segment);
  }, 0);
  
  // Calculate segment metrics
  const segmentLengthFt = segment.distance_ft || 
    Math.abs((segment.to_station_ft || 0) - (segment.from_station_ft || 0));
  const segmentLengthMiles = segmentLengthFt / 5280;
  
  // Calculate grinding ratio for this segment
  const segmentGrindRatio = segmentLengthFt > 0 ? grindingRequiredFt / segmentLengthFt : 0;
  const alrsPerMile = segmentLengthMiles > 0 ? overlappingALRs.length / segmentLengthMiles : 0;
  const maxSeverity = overlappingALRs.length > 0 ? Math.max(...overlappingALRs.map(e => e.magnitude_in || 0)) : 0;
  
  // ENHANCED: Calculate multi-factor ALR score
  const extentScore = getALRScoreFromGrindRatio(segmentGrindRatio);
  const severityScore = calculateSeverityScore(overlappingALRs, iriThreshold);
  const densityScore = calculateDensityScore(alrsPerMile);
  const criticalityScore = calculateCriticalityScore(maxSeverity, iriThreshold);
  
  // Weighted combination (extent 40%, severity 30%, density 20%, criticality 10%)
  const enhancedALRScore = (
    extentScore * 0.40 +
    severityScore * 0.30 +
    densityScore * 0.20 +
    criticalityScore * 0.10
  );
  
  return {
    // Basic ALR data
    alr_events_count: overlappingALRs.length,
    alr_events: overlappingALRs,
    grinding_required_ft: grindingRequiredFt,
    segment_length_ft: segmentLengthFt,
    segment_length_miles: segmentLengthMiles,
    grinding_ratio: segmentGrindRatio,
    grinding_percent: (segmentGrindRatio * 100).toFixed(2),
    has_grinding_required: grindingRequiredFt > 0,
    
    // Enhanced metrics
    alrs_per_mile: alrsPerMile,
    max_severity: maxSeverity,
    
    // Component scores
    extent_score: Math.round(extentScore),
    severity_score: Math.round(severityScore),
    density_score: Math.round(densityScore),
    criticality_score: Math.round(criticalityScore),
    
    // Final enhanced ALR score
    enhanced_alr_score: Math.round(enhancedALRScore),
    
    // Legacy compatibility
    alr_score: Math.round(enhancedALRScore) // Use enhanced score as default
  };
};

// ============================================================================
// UNIFIED SEGMENT HEALTH CALCULATION
// ============================================================================

// UNIFIED: Calculate comprehensive segment health score
export const calculateSegmentHealthScore = (segment, allEvents, context = {}) => {
  const start = segment.from_station_ft || 0;
  const end = segment.to_station_ft || (start + (segment.distance_ft || 0));
  const lengthFt = segment.distance_ft || Math.abs(end - start);
  const lengthMiles = lengthFt / 5280;
  const iri = segment.iri_avg_in_per_mi || 0;
  
  // Build thresholds
  const iriThreshold = context.iriThreshold || 160;
  const thresholds = buildThresholds(iriThreshold);
  const useNonLinear = context.useNonLinear !== false;
  
  // Calculate component scores
  const smoothnessScore = useNonLinear 
    ? getSmoothnessScoreNonLinear(iri, thresholds)
    : getSmoothnessBand(iri, thresholds);
    
  const consistencyScore = getConsistencyScore(segment);
  const confidenceScore = getConfidenceScore(segment, context.measurementQuality);
  
  // Calculate segment-specific ALR data with enhanced scoring
  const alrData = calculateSegmentALRData(segment, allEvents, iriThreshold);
  const alrScore = alrData.enhanced_alr_score;
  
  // Calculate final health score
  const weights = DEFAULT_WEIGHTS;
  const baseScore = (
    smoothnessScore * weights.smoothness +
    alrScore * weights.alr +
    consistencyScore * weights.consistency
  );
  
  // Apply trend adjustment if available
  const finalScore = context.historicalData && context.historicalData.length >= 2
    ? calculateTrendAdjustedScore(baseScore, context.historicalData)
    : baseScore;
  
  return {
    // FIXED: Preserve ALL original segment data
    ...segment, // This preserves geom, geometry, wkt_geom, and any other fields
    
    // Override/add calculated fields
    segment_id: segment.segment_id,
    from_station_ft: start,
    to_station_ft: end,
    distance_ft: lengthFt,
    distance_miles: lengthMiles,
    
    // IRI data (override if calculated values are better)
    iri_avg: iri,
    iri_left: segment.iri_left_in_per_mi || 0,
    iri_right: segment.iri_right_in_per_mi || 0,
    
    // Component scores
    smoothness_score: Math.round(smoothnessScore),
    alr_score: Math.round(alrScore),
    consistency_score: Math.round(consistencyScore),
    confidence_score: Math.round(confidenceScore),
    
    // Overall health
    health_score: Math.round(Math.min(100, Math.max(0, finalScore))),
    category: getHealthCategory(finalScore),
    
    // Enhanced ALR breakdown
    alr_data: alrData,
    
    // Metadata
    component_scores: {
      smoothness: Math.round(smoothnessScore),
      alr: Math.round(alrScore),
      consistency: Math.round(consistencyScore)
    },
    weights_used: weights,
    calculation_context: {
      iri_threshold: iriThreshold,
      use_enhanced_alr: true,
      use_non_linear: useNonLinear
    }
  };
};

// ============================================================================
// SESSION-LEVEL HEALTH CALCULATION (AGGREGATED FROM SEGMENTS)
// ============================================================================

// UNIFIED: Calculate session health by aggregating segment-level scores
export const calculateSessionHealthScore = (segments, events, context = {}) => {
  const iriThreshold = context.iriThreshold || 160;
  const useNonLinear = context.useNonLinear !== false;
  
  // Calculate individual segment scores
  const segmentScores = segments.map(segment => 
    calculateSegmentHealthScore(segment, events, {
      ...context,
      iriThreshold,
      useNonLinear
    })
  );

  // Aggregate session-level metrics
  const totalFt = segments.reduce((sum, s) => sum + (s.distance_ft || 0), 0);
  const totalMiles = totalFt / 5280;
  const totalSegments = segmentScores.length;

  if (totalSegments === 0) {
    return createEmptySessionResult();
  }

  // Calculate distance-weighted overall score
  const weightedScore = totalFt > 0
    ? segmentScores.reduce((sum, s) => sum + s.health_score * s.distance_ft, 0) / totalFt
    : segmentScores.reduce((sum, s) => sum + s.health_score, 0) / totalSegments;

  // Aggregate ALR data from all segments
const sessionALRData = aggregateALRDataFromSegments(segmentScores, totalMiles, events, iriThreshold);
  
  // Calculate distribution
  const distribution = calculateHealthDistribution(segmentScores);

  // Calculate averages (distance-weighted where applicable)
  const avgIRI = totalFt > 0
    ? segmentScores.reduce((sum, s) => sum + s.iri_avg * s.distance_ft, 0) / totalFt
    : segmentScores.reduce((sum, s) => sum + s.iri_avg, 0) / totalSegments;
    
  const avgSmoothness = totalFt > 0
    ? segmentScores.reduce((sum, s) => sum + s.smoothness_score * s.distance_ft, 0) / totalFt
    : segmentScores.reduce((sum, s) => sum + s.smoothness_score, 0) / totalSegments;
    
  const avgConsistency = totalFt > 0
    ? segmentScores.reduce((sum, s) => sum + s.consistency_score * s.distance_ft, 0) / totalFt
    : segmentScores.reduce((sum, s) => sum + s.consistency_score, 0) / totalSegments;
    
  const avgConfidence = totalFt > 0
    ? segmentScores.reduce((sum, s) => sum + s.confidence_score * s.distance_ft, 0) / totalFt
    : segmentScores.reduce((sum, s) => sum + s.confidence_score, 0) / totalSegments;

  // Get all grinding events for compatibility
  const allGrindingEvents = events.filter(ev =>
    ev.event_type === 'LR' && (ev.magnitude_in || 0) > iriThreshold
  );

  return {
    // Overall health metrics
    overall_health_score: Math.round(weightedScore),
    overall_category: getHealthCategory(weightedScore),
    
    // Component averages
    avg_smoothness_score: Math.round(avgSmoothness),
    avg_alr_score: Math.round(sessionALRData.overall_score),
    avg_consistency_score: Math.round(avgConsistency),
    avg_confidence_score: Math.round(avgConfidence),
    
    // Session metrics
    total_segments: totalSegments,
    total_miles: totalMiles,
    grinding_required_ft: Math.round(sessionALRData.total_grinding_ft),
    grinding_percent: (sessionALRData.grind_ratio * 100).toFixed(2),
    grinding_events: allGrindingEvents,
    total_alr_events: sessionALRData.total_alr_events,
    avg_iri: avgIRI,
    iri_threshold_used: iriThreshold,
    
    // Distribution and segments
    distribution_data: distribution.data,
    distribution_counts: distribution.counts,
    segment_scores: segmentScores,
    
    // Enhanced ALR breakdown
    enhanced_alr_data: sessionALRData,
    
    // Metadata
    calculated_at: new Date().toISOString(),
    context_used: {
      useNonLinear,
      useEnhancedALR: true,
      segment_specific_alr: true,
      weights: DEFAULT_WEIGHTS
    },
    thresholds: {
      iri: buildThresholds(iriThreshold),
      alr: {
        excellent: "≤ 0.5%",
        good: "0.6% - 2%", 
        fair: "2.1% - 5%",
        poor: "5.1% - 8%",
        very_poor: "8.1% - 12%",
        critical: "> 12%"
      }
    }
  };
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Aggregate ALR data from segment scores
const aggregateALRDataFromSegments = (segmentScores, totalMiles, allEvents, iriThreshold) => {
  const totalGrindingFt = segmentScores.reduce((sum, s) => 
    sum + s.alr_data.grinding_required_ft, 0
  );
  
  // Get unique ALR events (not double-counted across segments)
  const uniqueALREvents = allEvents.filter(ev =>
    ev.event_type === 'LR' && (ev.magnitude_in || 0) > iriThreshold
  );
  const totalALREvents = uniqueALREvents.length;
  
  const grindRatio = totalMiles > 0 ? (totalGrindingFt / 5280) / totalMiles : 0;
  const alrsPerMile = totalMiles > 0 ? totalALREvents / totalMiles : 0;
  const maxSeverity = segmentScores.reduce((max, s) => 
    Math.max(max, s.alr_data.max_severity || 0), 0
  );
  
  // Calculate session-level component scores
  const sessionExtentScore = getALRScoreFromGrindRatio(grindRatio);
  const sessionSeverityScore = segmentScores.length > 0
    ? segmentScores.reduce((sum, s) => sum + s.alr_data.severity_score, 0) / segmentScores.length
    : 100;
  const sessionDensityScore = calculateDensityScore(alrsPerMile);
  const sessionCriticalityScore = calculateCriticalityScore(maxSeverity, iriThreshold);
  
  const sessionALRScore = (
    sessionExtentScore * 0.40 +
    sessionSeverityScore * 0.30 +
    sessionDensityScore * 0.20 +
    sessionCriticalityScore * 0.10
  );
  
  return {
    overall_score: sessionALRScore,
    components: {
      extent: Math.round(sessionExtentScore),
      severity: Math.round(sessionSeverityScore),
      density: Math.round(sessionDensityScore),
      criticality: Math.round(sessionCriticalityScore)
    },
    metrics: {
      grind_ratio: grindRatio,
      alrs_per_mile: alrsPerMile,
      max_severity: maxSeverity,
      grinding_required_ft: totalGrindingFt
    },
    total_grinding_ft: totalGrindingFt,
    total_alr_events: totalALREvents
  };
};

// Calculate health distribution from segment scores
const calculateHealthDistribution = (segmentScores) => {
  const counts = {
    EXCELLENT: segmentScores.filter(s => s.health_score >= 90).length,
    GOOD: segmentScores.filter(s => s.health_score >= 80 && s.health_score < 90).length,
    FAIR: segmentScores.filter(s => s.health_score >= 70 && s.health_score < 80).length,
    POOR: segmentScores.filter(s => s.health_score >= 60 && s.health_score < 70).length,
    VERY_POOR: segmentScores.filter(s => s.health_score < 60).length
  };

  const totalSegments = segmentScores.length;
  const data = Object.entries(counts).map(([key, value]) => {
    const cat = HEALTH_CATEGORIES[key];
    return {
      name: cat.label,
      value,
      percentage: totalSegments > 0 ? (value / totalSegments * 100).toFixed(1) : '0',
      color: cat.color
    };
  }).filter(d => d.value > 0);

  return { data, counts };
};

// Create empty session result for error cases
const createEmptySessionResult = () => ({
  overall_health_score: 0,
  overall_category: HEALTH_CATEGORIES.VERY_POOR,
  avg_smoothness_score: 0,
  avg_alr_score: 0,
  avg_consistency_score: 0,
  avg_confidence_score: 0,
  total_segments: 0,
  total_miles: 0,
  grinding_required_ft: 0,
  grinding_percent: '0.00',
  grinding_events: [],
  total_alr_events: 0,
  avg_iri: 0,
  distribution_data: [],
  distribution_counts: { EXCELLENT: 0, GOOD: 0, FAIR: 0, POOR: 0, VERY_POOR: 0 },
  segment_scores: [],
  enhanced_alr_data: {
    overall_score: 0,
    components: { extent: 0, severity: 0, density: 0, criticality: 0 },
    metrics: { grind_ratio: 0, alrs_per_mile: 0, max_severity: 0, grinding_required_ft: 0 }
  },
  calculated_at: new Date().toISOString(),
  context_used: { useNonLinear: true, useEnhancedALR: true, segment_specific_alr: true }
});

// Trend analysis for historical data
export const calculateTrendAdjustedScore = (currentScore, historicalData = []) => {
  if (!historicalData || historicalData.length < 2) return currentScore;
  
  const n = historicalData.length;
  const sumX = historicalData.reduce((sum, _, i) => sum + i, 0);
  const sumY = historicalData.reduce((sum, score) => sum + score, 0);
  const sumXY = historicalData.reduce((sum, score, i) => sum + (i * score), 0);
  const sumX2 = historicalData.reduce((sum, _, i) => sum + (i * i), 0);
  
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const trendMultiplier = slope > 0 ? 1.05 : (slope < -2 ? 0.95 : 1.0);
  
  return Math.min(100, Math.max(0, currentScore * trendMultiplier));
};

// Get health category helper
export const getHealthCategory = (score) => {
  if (score >= HEALTH_CATEGORIES.EXCELLENT.min) return HEALTH_CATEGORIES.EXCELLENT;
  if (score >= HEALTH_CATEGORIES.GOOD.min) return HEALTH_CATEGORIES.GOOD;
  if (score >= HEALTH_CATEGORIES.FAIR.min) return HEALTH_CATEGORIES.FAIR;
  if (score >= HEALTH_CATEGORIES.POOR.min) return HEALTH_CATEGORIES.POOR;
  return HEALTH_CATEGORIES.VERY_POOR;
};

export const getWeights = () => ({ ...DEFAULT_WEIGHTS });

// ============================================================================
// LEGACY COMPATIBILITY FUNCTIONS
// ============================================================================

// Maintain backward compatibility with existing function names
export const calculateEnhancedSessionHealthScore = calculateSessionHealthScore;
export const calculateEnhancedSessionHealthScoreWithSegmentALR = calculateSessionHealthScore;
export const calculateEnhancedSegmentHealthScore = calculateSegmentHealthScore;
export const calculateEnhancedSegmentHealthScoreWithALR = calculateSegmentHealthScore;
//export const calculateSegmentALRData = calculateSegmentALRData;
export const buildSmoothnessThresholds = buildThresholds;